import { useNavigate } from "react-router-dom";
import { LogoMark } from "@/components/LogoMark";
import { Printer, ArrowLeft, ArrowRight, Check, ShieldCheck, ScanLine, GitCompare, FileCheck2, Users, Database, Percent } from "lucide-react";

const steps = [
  { icon: ScanLine, t: "Validate", d: "Check incoming ADAC XML against the schema and your acceptance rules." },
  { icon: GitCompare, t: "Compare", d: "Diff resubmissions against the original to see exactly what changed." },
  { icon: ShieldCheck, t: "Standardise", d: "Apply one consistent acceptance standard across every reviewer." },
  { icon: FileCheck2, t: "Report", d: "Export audit-ready validation reports for a defensible paper trail." },
];

const benefits = [
  "Validate submissions in minutes, not days",
  "Reject non-compliant data before it enters your asset systems",
  "One standard across reviewers, projects and contractors",
  "Give consultants the exact standard you validate against",
  "Fewer resubmission cycles and faster handover sign-off",
  "A clear, auditable record for every accepted submission",
];

const chain = [
  { icon: Users, t: "Consultant creates ADAC" },
  { icon: ScanLine, t: "Pre-validates to your standard" },
  { icon: ShieldCheck, t: "Council validates & accepts" },
  { icon: Database, t: "Clean data into asset systems" },
];

export default function CouncilsBrief() {
  const navigate = useNavigate();
  return (
    <div data-testid="councils-brief-page" className="min-h-screen bg-zinc-100 print:bg-white">
      {/* Action bar — hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="brief-back-button"
            onClick={() => navigate("/for-councils")}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-[#0033A0]"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            data-testid="print-brief-button"
            onClick={() => window.print()}
            aria-label="Download or print the Council capability statement as PDF"
            className="inline-flex items-center gap-2 bg-[#0033A0] text-white hover:bg-[#00257A] transition-colors px-5 py-2 text-sm font-medium rounded-sm"
          >
            <Printer size={16} /> Download / Print PDF
          </button>
        </div>
      </div>

      {/* A4-style document */}
      <div className="max-w-3xl mx-auto my-8 print:my-0 bg-white shadow-sm print:shadow-none p-10 md:p-14">
        <header className="flex items-center justify-between border-b border-zinc-200 pb-6">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 grid place-items-center bg-[#0033A0] text-white">
              <LogoMark size={20} />
            </span>
            <span className="font-heading text-xl font-semibold tracking-tight text-zinc-900">WebADAC</span>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#0033A0]">Council Capability Statement</p>
        </header>

        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#0033A0]">For LGAs &amp; Governing Bodies</p>

        <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-zinc-900 leading-tight">
          Consistent ADAC data across your entire submission chain.
        </h1>
        <p className="mt-4 text-zinc-600 leading-relaxed">
          WebADAC gives Local Government Authorities and governing bodies one consistent acceptance standard for every
          as-constructed ADAC submission — from the consultant's desk to your asset systems. Standardise what your
          suppliers submit, cut rework and resubmissions, and keep an audit-ready record for every handover.
        </p>

        <div className="mt-8 rounded-xl border border-zinc-200 p-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#0033A0] mb-4">The submission chain</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {chain.map((c, i) => (
              <div key={c.t} className="flex items-center gap-2 flex-1">
                <div className="flex items-center gap-2.5 rounded-lg bg-[#E6EBF5]/50 px-3 py-2.5 flex-1">
                  <span className="shrink-0 h-7 w-7 grid place-items-center bg-[#0033A0] text-white rounded-sm">
                    <c.icon size={15} strokeWidth={1.7} />
                  </span>
                  <span className="text-xs font-medium text-zinc-800 leading-tight">{c.t}</span>
                </div>
                {i < chain.length - 1 && <ArrowRight size={16} className="hidden sm:block text-zinc-400 shrink-0" />}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            One standard applied end-to-end — so what consultants build is exactly what your Council accepts.
          </p>
        </div>

        <h2 className="mt-10 font-heading text-lg font-medium text-zinc-900">How Councils use WebADAC</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {steps.map((s) => (
            <div key={s.t} className="flex items-start gap-3 rounded-lg border border-zinc-200 p-4">
              <span className="shrink-0 h-9 w-9 grid place-items-center bg-[#E6EBF5] text-[#0033A0] rounded-sm">
                <s.icon size={18} strokeWidth={1.7} />
              </span>
              <div>
                <p className="font-heading font-medium text-zinc-900">{s.t}</p>
                <p className="mt-0.5 text-sm text-zinc-600 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-10 font-heading text-lg font-medium text-zinc-900">Outcomes for your Council</h2>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {benefits.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm text-zinc-700">
              <Check size={15} className="shrink-0 mt-0.5 text-[#0033A0]" /> {b}
            </li>
          ))}
        </ul>

        <div className="mt-10 rounded-xl border-2 border-[#0033A0]/25 bg-[#E6EBF5]/40 p-7">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center bg-[#0033A0] text-white rounded-sm">
              <Percent size={16} strokeWidth={1.8} />
            </span>
            <h2 className="font-heading text-lg font-medium text-zinc-900">Fund the standard, reward your suppliers</h2>
          </div>
          <p className="mt-3 text-sm text-zinc-700 leading-relaxed">
            When your LGA holds an annual WebADAC license, the consultants who submit to you can licence at the
            discounted <span className="font-medium text-zinc-900">annual rate on month-to-month terms — no lock-in</span>.
            Adopting your acceptance standard then costs your suppliers <span className="font-medium text-zinc-900">less, not more</span>.
            You get consistent data across every supplier; they get cheaper, simpler compliance.
          </p>
          <ul className="mt-4 grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-zinc-700">
            <li className="flex items-start gap-2"><Check size={15} className="shrink-0 mt-0.5 text-[#0033A0]" /> Council-issued discount codes</li>
            <li className="flex items-start gap-2"><Check size={15} className="shrink-0 mt-0.5 text-[#0033A0]" /> Lower cost to comply with your standard</li>
            <li className="flex items-start gap-2"><Check size={15} className="shrink-0 mt-0.5 text-[#0033A0]" /> Cleaner submissions, fewer rejections</li>
          </ul>
        </div>

        <div className="mt-6 rounded-xl bg-[#121316] text-white p-7">
          <h2 className="font-heading text-lg font-medium">The adoption flywheel</h2>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
            When a Council standardises on WebADAC, consultants submitting to that Council pre-validate against the very
            same standard — driving cleaner submissions across your whole supplier base, automatically.
          </p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-zinc-200 pt-6">
          <div>
            <p className="font-heading font-medium text-zinc-900">Book a 30-minute walkthrough</p>
            <p className="text-sm text-zinc-600">See WebADAC validate one of your own ADAC files.</p>
          </div>
          <p className="font-mono text-sm text-[#0033A0]">webadac.com → Book a demo</p>
        </div>

        <footer className="mt-10 border-t border-zinc-200 pt-5 flex items-center justify-between">
          <p className="font-mono text-[10px] text-zinc-500">© {new Date().getFullYear()} WebADAC · ADAC XML workflow platform</p>
          <p className="font-mono text-[10px] text-zinc-500">
            Powered by <span style={{ color: "#d4af37", fontWeight: 800, letterSpacing: "0.02em" }}>CBKM</span>
            <span className="text-zinc-400">.au</span>
          </p>
        </footer>
      </div>
    </div>
  );
}
